<script lang="ts">
  import { Icon } from '$ui/primitives/Icon';
  import { toast } from '$ui/primitives/Toast';
  import { createNode } from '$db/local/repositories';

  let { onImportSuccess }: { onImportSuccess?: () => void } = $props();

  interface GenericNode {
    title: string;
    type?: string;
    children?: GenericNode[];
  }

  let isDragging = $state(false);
  let isProcessing = $state(false);
  let errorMsg = $state<string | null>(null);
  let parsedData = $state<GenericNode[] | null>(null);
  let pastedJson = $state('');

  function isValidNode(item: any): item is GenericNode {
    return typeof item === 'object' && item !== null && (typeof item.title === 'string' || typeof item.subject === 'string');
  }

  async function handleJsonText(text: string) {
    errorMsg = null;
    parsedData = null;

    try {
      const json = JSON.parse(text);

      if (!Array.isArray(json) || json.length === 0) {
        errorMsg = 'The JSON must be an array of items.';
        return;
      }

      const validRoots = json.filter(isValidNode);
      if (validRoots.length === 0) {
        errorMsg = 'No valid data found. Check the schema format.';
        return;
      }

      // Adaptación de formato antiguo (subject/subtopics) a formato genérico
      const normalize = (node: any, depth: number): GenericNode => {
        const title = node.title || node.subject || node.name || 'Untitled';
        let children: GenericNode[] = [];
        
        if (node.children) {
          children = node.children.map((c: any) => normalize(c, depth + 1));
        } else if (node.subtopics) {
          children = node.subtopics.map((c: any) => normalize(c, depth + 1));
        } else if (node.books) {
          children = node.books.map((c: any) => normalize(c, depth + 1));
        } else if (node.chapters) {
          children = node.chapters.map((c: any) => 
            typeof c === 'string' ? { title: c } : normalize(c, depth + 1)
          );
        }

        const explicitType = node.type ? String(node.type).trim() : null;
        const defaultType = depth === 0 ? 'subject' : 'topic';
        const finalType = explicitType || defaultType;

        return { title, type: finalType, children };
      };

      parsedData = validRoots.map((r: any) => normalize(r, 0));
      await confirmImport();
    } catch (err) {
      console.error(err);
      errorMsg = 'Could not parse the JSON. Make sure it is valid JSON.';
      isProcessing = false;
    }
  }

  async function processFile(file: File) {
    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      errorMsg = 'Please upload a valid JSON file.';
      return;
    }
    try {
      const text = await file.text();
      await handleJsonText(text);
    } catch (err) {
      errorMsg = 'Could not read the file.';
    }
  }

  function processPastedJson() {
    if (!pastedJson.trim()) return;
    void handleJsonText(pastedJson);
  }

  function handleFileDrop(e: DragEvent) {
    isDragging = false;
    const file = e.dataTransfer?.files[0];
    if (file) processFile(file);
  }

  function handleFileSelect(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) processFile(file);
  }

  async function insertNode(node: GenericNode, parentId: string | null) {
    const type = node.type || (parentId ? 'item' : 'subject');
    const created = await createNode(parentId, node.title, type);
    
    if (node.children) {
      for (const child of node.children) {
        await insertNode(child, created.id);
      }
    }
  }

  async function confirmImport() {
    if (!parsedData) return;
    isProcessing = true;
    let totalNodes = 0;

    try {
      for (const root of parsedData) {
        await insertNode(root, null);
        totalNodes++;
      }

      toast.success(`Successfully imported curriculum tree!`);
      parsedData = null;
      onImportSuccess?.();
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (e) {
      console.error(e);
      toast.error('Error saving imported data.');
      isProcessing = false;
    }
  }

  const AI_PROMPT = `Act as an expert Data Architect. I will provide a study syllabus in unstructured text.
Your task is to transform it into a strict hierarchical JSON tree structure.

The schema must follow this exact format:
[
  {
    "title": "Root Name (e.g., Copywriting, Mathematics)",
    "type": "Subject",
    "children": [
      {
        "title": "Area 1 Name",
        "type": "Área",
        "children": [
          {
            "title": "Module 1 Name",
            "type": "Módulo",
            "children": [
              { "title": "Resource Name", "type": "Recurso" }
            ]
          }
        ]
      }
    ]
  }
]

CRITICAL RULES:
1. Output ONLY the valid JSON block. No markdown, no explanations, no extra text.
2. Every single node MUST have a "title" field (string). This is the literal name of the item.
3. Every single node MUST have a "type" field (string). This is the structural level of the node. Use the EXACT terminology provided in the syllabus (e.g., "Subject", "Área", "Módulo", "Lección", "Recurso"). 
4. The "children" key is ONLY used if that node has sub-items. If it doesn't, omit the "children" key entirely.
5. DEPTH IS UNLIMITED. Expand the hierarchy to as many levels as the syllabus requires. Do not stop early.
6. Do NOT duplicate data. Group all items hierarchically using "children".
7. Do NOT invent or rename items. Use the EXACT names provided in the syllabus.
8. Do not add any other fields besides "title", "type", and "children".`;
</script>

<div class="import-panel">
  <div class="panel-header">
    <h3 class="panel-title">AI-Assisted Curriculum Import</h3>
    <p class="hint">Import your entire subject tree instantly. Existing items will not be duplicated.</p>
  </div>

  <div class="prompt-box">
    <div class="prompt-header">
      <strong>Step 1:</strong> Copy this prompt and paste it into ChatGPT/Claude along with your syllabus text.
      <button class="copy-btn" onclick={() => { navigator.clipboard.writeText(AI_PROMPT); toast.success('Prompt copied!'); }}>
        <Icon name="copy" size={14} /> Copy Prompt
      </button>
    </div>
    <textarea readonly class="prompt-text">{AI_PROMPT}</textarea>
  </div>

  <div class="dropzone-wrapper">
    <p><strong>Step 2:</strong> Drag and drop the generated JSON file here:</p>
    <div 
      class="dropzone" 
      class:dragging={isDragging}
      role="button"
      tabindex="0"
      ondragover={(e) => { e.preventDefault(); isDragging = true; }}
      ondragleave={() => isDragging = false}
      ondrop={(e) => { e.preventDefault(); handleFileDrop(e); }}
    >
      <input type="file" accept=".json" onchange={handleFileSelect} class="file-input" />
      <span class="upload-icon"><Icon name="upload" size={32} /></span>
      <p>Drag & drop your JSON file or <span class="browse-link">browse</span></p>
    </div>
  </div>

  <div class="paste-json-wrapper">
    <p><strong>Or paste the JSON directly here:</strong></p>
    <textarea 
      bind:value={pastedJson} 
      placeholder="Paste your JSON here..."
      class="json-textarea"
    ></textarea>
    <button 
      class="process-btn" 
      onclick={processPastedJson} 
      disabled={!pastedJson.trim() || isProcessing}
    >
      <Icon name="check" size={16} /> Import Pasted JSON
    </button>
  </div>

  {#if errorMsg}
    <p class="error-msg">{errorMsg}</p>
  {/if}

  {#if isProcessing}
    <div class="preview-box" style="border-color: var(--color-brand); background: var(--color-brand-tint);">
      <p class="success-msg" style="color: var(--color-brand);"><Icon name="activity" size={16} /> Importing curriculum, please wait...</p>
    </div>
  {/if}
</div>

<style>
  .import-panel {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    background: var(--color-surface-sunken);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    padding: var(--space-5);
    margin-top: var(--space-3);
    min-width: 0;
    width: 100%;
  }

  .panel-header { display: flex; flex-direction: column; gap: 4px; }
  .panel-title { margin: 0; font-size: var(--text-h3); font-weight: 600; color: var(--color-text-primary); }

  .prompt-box {
    display: flex; flex-direction: column; gap: var(--space-2);
    background: var(--color-surface); border: 1px solid var(--color-border);
    border-radius: var(--radius-md); padding: var(--space-3);
    min-width: 0; width: 100%;
  }

  .prompt-header {
    display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center;
    gap: var(--space-2); font-size: var(--text-sm); color: var(--color-text-secondary);
  }

  .copy-btn {
    display: flex; align-items: center; gap: 4px;
    background: var(--color-surface-elevated); border: 1px solid var(--color-border);
    padding: 6px 10px; border-radius: var(--radius-sm); font-size: var(--text-xs);
    cursor: pointer; color: var(--color-text-primary); transition: all 0.2s;
  }
  .copy-btn:hover { background: var(--color-surface-hover); }

  .prompt-text {
    width: 100%; height: 160px; padding: var(--space-2);
    font-family: var(--font-mono); font-size: var(--text-xs);
    color: var(--color-text-muted); background: var(--color-surface-sunken);
    border: 1px solid var(--color-border); border-radius: var(--radius-sm);
    resize: none; white-space: pre-wrap;
  }

  .dropzone-wrapper { display: flex; flex-direction: column; gap: var(--space-2); font-size: var(--text-sm); color: var(--color-text-secondary); }
  
  .paste-json-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
  }
  
  .json-textarea {
    width: 100%;
    height: 100px;
    padding: var(--space-2);
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    color: var(--color-text-primary);
    background: var(--color-surface-sunken);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    resize: vertical;
  }
  
  .process-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: var(--space-2) var(--space-4);
    background: var(--color-brand);
    color: var(--color-brand-fg);
    border: none;
    border-radius: var(--radius-sm);
    font-weight: 600;
    font-size: var(--text-sm);
    cursor: pointer;
    transition: opacity 0.2s;
  }
  
  .process-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .dropzone {
    border: 2px dashed var(--color-border-strong); border-radius: var(--radius-md);
    padding: var(--space-6) var(--space-4); text-align: center; position: relative;
    transition: all 0.2s; background: var(--color-surface); cursor: pointer;
  }
  .dropzone.dragging { border-color: var(--color-brand); background: var(--color-brand-tint); }
  .file-input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
  .upload-icon { display: flex; justify-content: center; color: var(--color-text-muted); margin-bottom: var(--space-2); }
  .browse-link { color: var(--color-brand); font-weight: 600; text-decoration: underline; }
  .error-msg { color: var(--color-danger); font-size: var(--text-sm); margin: 0; }

  .preview-box {
    display: flex; justify-content: space-between; align-items: center;
    background: var(--color-success-tint); border: 1px solid var(--color-success);
    padding: var(--space-3); border-radius: var(--radius-md);
  }
  .success-msg { color: var(--color-success); font-size: var(--text-sm); font-weight: 600; display: flex; align-items: center; gap: 6px; margin: 0; }
</style>